import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import {
  CapGainsEstimatesFilterType,
  CapGainsEstimatesAction,
  CapGainsEstimatesListing,
  CapGainsEstimatesRecordDetails,
  CapGainsEstimatesSelectedFilters,
  CapGainsEstimatesRowSelection,
  CapGainsEstimatesColumnSortDetails,
} from '@products/types/tax.type';
import {
  BrBreadcrumb,
  EDSPaginationPageChangedEvent,
  ProductDetailConfiguration,
  StickyFooterActionParam,
  SwitchState,
  SwitchStateFunds,
} from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subscription } from 'rxjs';
import { UsCapitalGainsEstimatesService } from '../services/us-capital-gains-estimates.service';
import { TaxDisplayComponent } from '../tax-component.interface';
import { TaxConfig } from '../tax.config';
import cloneDeep from 'lodash/cloneDeep';
import {
  BreadcrumbItem,
  DropdownItem,
  PaginationComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { WindowScrollService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import dayjs from 'dayjs';

const logger = Logger.getLogger('UsCapitalGainsEstimatesComponent');

@Component({
  selector: 'ft-us-capital-gains-estimates',
  templateUrl: './us-capital-gains-estimates.component.html',
  styleUrls: ['./us-capital-gains-estimates.component.scss'],
})
export class UsCapitalGainsEstimatesComponent
  extends BaseProductDetailComponent
  implements TaxDisplayComponent, OnInit, OnDestroy {
  dataSubscription: Subscription[] = [];
  breadcrumbItems: BreadcrumbItem[];
  filters: CapGainsEstimatesFilterType[];
  selectedFilters: CapGainsEstimatesSelectedFilters;
  recordDetails: CapGainsEstimatesRecordDetails;
  pageSizeItems: DropdownItem[] = [];
  selectedPageSizeItem: DropdownItem;
  currentPage$: Observable<CapGainsEstimatesListing[]>;
  currentPageNumber$: Observable<number>;
  asOfDate: string;
  totalPages: number;
  isHandheld$: Observable<boolean>;
  isLoaded: boolean;
  totalSelectedRows = 0;
  totalSelectedRowsAsText: string;
  downloadStickyButton: StickyFooterActionParam;
  showFavoritesOnlySwitch: boolean;
  favoritesOnlySwitchState: SwitchState; // To set default favorites only state - off/on.
  hideFundsSwitchState: SwitchStateFunds = SwitchStateFunds.YES;
  proximalTopColumnNumbers: object = {};
  records: CapGainsEstimatesListing[];
  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;

  constructor(
    private usCapitalGainsEstimatesService: UsCapitalGainsEstimatesService,
    private responsiveService: ResponsiveService,
    private scrollService: WindowScrollService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {
    super();

    // Create page size item list.
    this.pageSizeItems = ['25', '50', '75', '100'].map((rows) => ({
      value: rows,
      label: rows,
    }));

    // Initialize sticky footer download button params.
    this.downloadStickyButton = {
      hasAction: true,
      buttonLabel: this.translateService.instant(
        'products.capgains-excel-sticky-download-button'
      ),
      iconType: 'download',
    };
  }

  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();

    this.dataSubscription.push(
      this.usCapitalGainsEstimatesService
        .getTotalSelectedRows$()
        .subscribe((totalSelectedRows) => {
          this.totalSelectedRows = cloneDeep(totalSelectedRows);
          this.totalSelectedRowsAsText = `${
            this.totalSelectedRows
          } ${this.translateService.instant(
            'products.capgains-excel-sticky-item-selected'
          )}`;

          this.cdr.detectChanges();
        })
    );
  }

  populate(
    componentConfig: TaxConfig,
    config: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    // Initialize super.
    this.initialize(config, component, page);

    // Initialize available breadcrumb(s).
    this.breadcrumbItems = (component.getModels()
      ?.breadcrumbs as BrBreadcrumb[])?.map((breadcrumb) => ({
      content: breadcrumb.title,
      href: breadcrumb.url,
    }));
    this.asOfDate = this.usCapitalGainsEstimatesService.getLastBusinessDay(
      dayjs().year(),
      10
    );
    this.cdr.detectChanges();

    if (this.brConfig) {
      // Get subscription for estimates state.
      this.dataSubscription.push(
        this.usCapitalGainsEstimatesService
          .getCapGainsEstimatesState$()
          .subscribe(
            (state) => {
              this.usCapitalGainsEstimatesService.setHideFundEstimateFlag$(
                this.hideFundsSwitchState === SwitchStateFunds.YES
              );
              logger.debug('Cap gains estimates state: ', state);
              this.isLoaded = state.isLoaded;

              // Process listing tasks when 'showListing' flag is 'true' along with the state load.
              if (this.isLoaded && state.showListing) {
                // Assign filter details.
                if (state.action === CapGainsEstimatesAction.INITIAL_LOAD) {
                  this.filters = cloneDeep(state.filters);
                }

                // Assign page record details.
                this.recordDetails = cloneDeep(state.recordDetails);

                // Assign the pre selected item for page size dropdown.
                this.selectedPageSizeItem = this.pageSizeItems.find(
                  (pageSizeItem) =>
                    Number(pageSizeItem.value) === this.recordDetails.pageSize
                );
              }
              this.records = this.usCapitalGainsEstimatesService.getCurrentGridRecords();
              this.cdr.detectChanges();
            },
            (error) => {
              logger.debug('Error in fetching state: ', error);
              this.isLoaded = false;

              this.cdr.detectChanges();
            }
          ),
        // Get total number of pages for pagination bar.
        this.usCapitalGainsEstimatesService
          .getNumberOfPages$()
          .subscribe((totalPages) => {
            this.totalPages = totalPages;
            this.paginationComponent?.updatePagination(totalPages); // Explicitly update the pagination component with the updated count.
          }),
        // Get favorite only toggle details, when available.
        this.usCapitalGainsEstimatesService
          .getFavoritesToggleState$()
          .subscribe((favoritesState) => {
            this.showFavoritesOnlySwitch =
              favoritesState.showFavoritesOnlySwitch;
            this.favoritesOnlySwitchState =
              favoritesState.favoritesOnlySwitchState;

            // Assign correct column numbers for proximal top as per 'favoritesOnlySwitch' visibility.
            this.proximalTopColumnNumbers = this.showFavoritesOnlySwitch
              ? { s: '4', m: '8', l: '7' }
              : { s: '4', m: '8', l: '10' };

            this.cdr.detectChanges();
          })
      );

      // Get current page records as observable.
      this.currentPage$ = this.usCapitalGainsEstimatesService.getCurrentPage$();

      // Get current page number as observable.
      this.currentPageNumber$ = this.usCapitalGainsEstimatesService.getCurrentPageNumber$();

      // Invoke favorite toggle related processes in background.
      this.usCapitalGainsEstimatesService.handleFavoriteToggle();
    }
  }

  /**
   * Trigger page change with the page number selected.
   * @param event page changed event generated
   */
  onPageChanged(event: EDSPaginationPageChangedEvent): void {
    if (event.action !== 'load') {
      this.usCapitalGainsEstimatesService.goToPage(event.page);

      // Scroll to the target anchor once page is changed.
      this.scrollService.scrollToAnchor('capGainsScrollPoint');
    }
  }

  /**
   * Trigger page size change with the size selected.
   * @param selectedDropdownItem dropdown item selected
   */
  onPageSizeChanged(selectedDropdownItem: DropdownItem): void {
    this.selectedPageSizeItem = selectedDropdownItem;
    this.usCapitalGainsEstimatesService.setPageSize(
      Number(selectedDropdownItem.value)
    );

    // Scroll to the target anchor once page size is changed.
    this.scrollService.scrollToAnchor('capGainsScrollPoint');
  }

  /**
   * Trigger grid filter as per the filter selection(s).
   * @param selectedFilters selected dropdown or searched text
   */
  onFilterApplied(selectedFilters: CapGainsEstimatesSelectedFilters): void {
    // Update the class variable for future 'favoriteOnly' toggle operation.
    this.selectedFilters = selectedFilters;

    // And, send the updated details for further grid filter processes.
    this.usCapitalGainsEstimatesService.filterListing(
      selectedFilters,
      this.favoritesOnlySwitchState === SwitchState.ON,
      this.hideFundsSwitchState === SwitchStateFunds.YES
    );
  }

  /**
   * Trigger row selection / deselection as per the row detail provided.
   * @param rowSelection 'flag' value with record 'id'
   */
  onRowSelected(rowSelection: CapGainsEstimatesRowSelection): void {
    this.usCapitalGainsEstimatesService.updateRowSelection(rowSelection);
  }

  /**
   * Trigger full / selective grid data download.
   * @param isSelectiveDownload flag to be passed, if download action corresponds to selective rows
   */
  onDownloadAction(isSelectiveDownload?: boolean): void {
    this.usCapitalGainsEstimatesService.handleDownloadAction(
      isSelectiveDownload
    );
  }

  /**
   * Trigger favorites only listing as per toggle.
   * @param updatedSwitchState new switch state upon toggle action
   */
  onFavoritesOnlyToggle(updatedSwitchState: SwitchState): void {
    // Update the class variable for future 'filter' operation.
    this.favoritesOnlySwitchState = updatedSwitchState;

    // And, send the updated details for further grid filter processes.
    this.usCapitalGainsEstimatesService.filterListing(
      this.selectedFilters,
      updatedSwitchState === SwitchState.ON,
      this.hideFundsSwitchState === SwitchStateFunds.YES
    );
  }
  /**
   * Trigger Hide Funds with no estimates as per toggle.
   * @param updatedSwitchState new switch state upon toggle action
   */
  onHideFundsToggle(updatedSwitchState: SwitchStateFunds): void {
    // Update the class variable for future 'filter' operation.
    this.hideFundsSwitchState = updatedSwitchState;
    this.usCapitalGainsEstimatesService.filterListing(
      this.selectedFilters,
      this.favoritesOnlySwitchState === SwitchState.ON,
      updatedSwitchState === SwitchStateFunds.YES
    );
  }

  /**
   * Trigger sort on the target column as per the column detail passed.
   * @param colSortDetails details of the column to be sorted
   */
  onSortChanged(colSortDetails: CapGainsEstimatesColumnSortDetails): void {
    this.usCapitalGainsEstimatesService.handleGridSort(colSortDetails);
  }

  ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }
}
